/**
 * Configurações da viagem.
 *
 * A tela que faltava: o que se informa ao criar precisa poder ser revisto
 * depois — nome, destino, período, fuso, moeda e hospedagem. Sem ela, um erro
 * de digitação na criação virava um dado permanente da viagem.
 *
 * **Só quem tem `manage_trip_settings`.** Quem não tem vê os valores em leitura,
 * com o motivo — esconder a tela inteira faria a pessoa achar que o app não
 * tem o recurso.
 *
 * O período é o campo perigoso, e a tela é honesta sobre isso: ampliar cria os
 * dias novos e não custa nada; encurtar sobre dias que já têm programação é
 * recusado pelo servidor, que devolve **quais** dias. A tela mostra a lista e
 * deixa a decisão com quem organiza — nada é apagado por conta própria.
 *
 * Transporte não está aqui: é entidade própria, e as datas dele podem cair
 * fora do intervalo da viagem.
 */
import { TRIP_CURRENCIES, TRIP_CURRENCY_LABELS, TRIP_CURRENCY_LOCKED, type TripCurrency } from '@nv/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError, updateTrip, type DiaComConteudo } from '../../auth/api';
import { useAuthStore } from '../../auth/store';
import { usePermissao, useTrip } from '../../trip/TripContext';
import { guardarViagemAtiva } from '../../trip/viagemAtiva';
import { Card, Note, SectionLabel, Segmented } from '../../ui/primitives';
import { Field } from '../auth/Field';
import { LocalField, type LugarDaAtividade } from '../roteiro/LocalField';
import { SairDaViagem } from './SairDaViagem';
import { CapaDaViagem } from './CapaDaViagem';
import { FuncionalidadesDaViagem } from './FuncionalidadesDaViagem';
import { FUSOS } from './opcoes';

export function ConfiguracoesViagemScreen() {
  const cliente = useQueryClient();
  const { trip } = useTrip();
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const podeEditar = usePermissao('manage_trip_settings');

  const [nome, setNome] = useState(trip.name);
  const [destino, setDestino] = useState(trip.primaryDestination);
  const [inicio, setInicio] = useState(trip.startDate);
  const [fim, setFim] = useState(trip.endDate);
  const [fuso, setFuso] = useState(trip.timezone);
  const [moeda, setMoeda] = useState<TripCurrency>(trip.baseCurrency as TripCurrency);
  const [hospedagem, setHospedagem] = useState(trip.accommodationName ?? '');
  const [horaDoResumo, setHoraDoResumo] = useState(trip.summaryTime ?? '21:00');
  const [endereco, setEndereco] = useState(trip.accommodationAddress ?? '');
  const [checkIn, setCheckIn] = useState(trip.accommodationCheckIn ?? '');
  const [checkOut, setCheckOut] = useState(trip.accommodationCheckOut ?? '');
  const [instrucoes, setInstrucoes] = useState(trip.accommodationNotes ?? '');
  const [lugarDaHospedagem, setLugarDaHospedagem] = useState<LugarDaAtividade | null>(
    trip.accommodationPlaceId && trip.accommodationLatitude !== null && trip.accommodationLongitude !== null
      ? {
          placeId: trip.accommodationPlaceId,
          placeAddress: trip.accommodationAddress,
          latitude: trip.accommodationLatitude,
          longitude: trip.accommodationLongitude,
        }
      : null,
  );

  const [erro, setErro] = useState<string | null>(null);
  const [bloqueados, setBloqueados] = useState<DiaComConteudo[]>([]);
  const [salvo, setSalvo] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const opcoesDeFuso = [...new Set([trip.timezone, ...FUSOS])];
  const datasInvertidas = inicio.length > 0 && fim.length > 0 && fim < inicio;

  const podeSalvar =
    podeEditar &&
    nome.trim().length > 0 &&
    destino.trim().length > 0 &&
    inicio.length > 0 &&
    fim.length > 0 &&
    !datasInvertidas;

  async function salvar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeSalvar) return;

    setEnviando(true);
    setErro(null);
    setBloqueados([]);
    setSalvo(false);

    try {
      const atualizada = await updateTrip(trip.id, {
        name: nome.trim(),
        primaryDestination: destino.trim(),
        startDate: inicio,
        endDate: fim,
        timezone: fuso,
        baseCurrency: moeda,
        accommodationName: hospedagem.trim() || null,
        accommodationAddress: endereco.trim() || null,
        accommodationPlaceId: lugarDaHospedagem?.placeId ?? null,
        accommodationLatitude: lugarDaHospedagem?.latitude ?? null,
        accommodationLongitude: lugarDaHospedagem?.longitude ?? null,
        accommodationCheckIn: checkIn || null,
        accommodationCheckOut: checkOut || null,
        accommodationNotes: instrucoes.trim() || null,
        summaryTime: horaDoResumo,
      });

      guardarViagemAtiva(userId, atualizada);
      // Os dias mudaram junto com o período: a lista em cache ficaria velha.
      await cliente.invalidateQueries({ queryKey: ['viagens'] });
      await cliente.invalidateQueries({ queryKey: ['viagem', 'dias', trip.id] });
      setSalvo(true);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'dias_com_conteudo') {
        setBloqueados((e.detalhe['days'] as DiaComConteudo[] | undefined) ?? []);
        setErro(null);
      } else {
        setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar agora.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section className="viagens">
      <header className="fin-nova__header">
        <h1 className="viagens__titulo">Configurações da viagem</h1>
      </header>

      {!podeEditar && (
        <Card variant="chip">
          <p className="auth-note" role="status">
            Você pode ver as configurações desta viagem, mas não alterá-las. Peça
            a quem administra.
          </p>
        </Card>
      )}

      <form className="auth-form" onSubmit={salvar}>
        <CapaDaViagem tripId={trip.id} nome={nome} podeEditar={podeEditar} />
        <Field
          label="Nome da viagem"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          disabled={!podeEditar}
          required
        />
        <LocalField rotulo="Destino principal" modo="destino" nome={destino} lugar={null} onChange={(texto) => setDestino(texto)} disabled={!podeEditar} required />

        <div className="auth-form__row">
          <Field
            label="Início"
            type="date"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
            disabled={!podeEditar}
            required
          />
          <Field
            label="Término"
            type="date"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            disabled={!podeEditar}
            required
            error={datasInvertidas ? 'O término não pode ser antes do início.' : undefined}
          />
        </div>

        <p className="auth-note">
          Ampliar o período cria os dias novos, vazios. Encurtar só remove dias
          sem programação.
        </p>

        <SectionLabel>FUSO DA VIAGEM</SectionLabel>
        <Card variant="surf">
          <select
            className="fin-nova__select"
            value={fuso}
            onChange={(e) => setFuso(e.target.value)}
            disabled={!podeEditar}
            aria-label="Fuso da viagem"
          >
            {opcoesDeFuso.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </Card>

        <SectionLabel>MOEDA DA VIAGEM</SectionLabel>
        <Card variant="surf">
          {/* Com dados financeiros a moeda trava: trocar reinterpretaria os
              valores, e o app não converte. O servidor também recusa (409). */}
          <div className={trip.currencyLocked || !podeEditar ? 'nv-segmented--travado' : undefined} data-testid="moeda-da-viagem" data-travada={trip.currencyLocked ? 'sim' : 'nao'}>
            <Segmented
              value={moeda}
              options={TRIP_CURRENCIES.map((m) => ({ valor: m, rotulo: `${TRIP_CURRENCY_LABELS[m].simbolo} ${m}`, testId: `moeda-${m}` }))}
              onChange={(m) => {
                if (!trip.currencyLocked && podeEditar) setMoeda(m);
              }}
              label="Moeda da viagem"
            />
          </div>
          {trip.currencyLocked && <Note>{TRIP_CURRENCY_LOCKED}</Note>}
        </Card>

        <SectionLabel>FUNCIONALIDADES</SectionLabel>
        <FuncionalidadesDaViagem podeEditar={podeEditar} />

        <SectionLabel>RESUMO DO DIA</SectionLabel>
        <label className="field">
          <span className="field__label">Hora em que o resumo fica pronto</span>
          <input className="field__input" type="time" value={horaDoResumo} onChange={(e) => setHoraDoResumo(e.target.value)} disabled={!podeEditar} aria-label="Hora do resumo do dia" data-testid="hora-do-resumo" />
        </label>
        <p className="auth-note">No fuso da viagem. Nessa hora o grupo recebe "O dia de hoje está pronto".</p>

        <SectionLabel>HOSPEDAGEM</SectionLabel>
        <Field
          label="Nome da hospedagem"
          value={hospedagem}
          onChange={(e) => setHospedagem(e.target.value)}
          disabled={!podeEditar}
          placeholder="Onde o grupo fica"
        />
        {/* Escolher pela busca põe a hospedagem no mapa; digitar à mão vale
            como endereço, sem pin. */}
        <LocalField
          rotulo="Endereço"
          modo="endereco"
          nome={endereco}
          lugar={lugarDaHospedagem}
          disabled={!podeEditar}
          onChange={(texto, lugar) => {
            setEndereco(texto);
            setLugarDaHospedagem(lugar);
          }}
        />
        <div className="auth-form__row">
          <Field label="Check-in" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} disabled={!podeEditar} data-testid="hosp-checkin" />
          <Field label="Check-out" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} disabled={!podeEditar} data-testid="hosp-checkout" />
        </div>
        <label className="nv-field">
          <span className="nv-field__label">Instruções</span>
          <textarea className="nv-field__input fb__texto" rows={3} value={instrucoes} onChange={(e) => setInstrucoes(e.target.value)} disabled={!podeEditar} placeholder="Código da porta, Wi-Fi, onde fica a chave…" data-testid="hosp-instrucoes" />
        </label>
        <p className="auth-note">
          Quem participa da viagem vê isto na Home, mesmo sem sinal. Transporte
          tem tela própria: os horários dele podem ficar fora do período da viagem.
        </p>

        {bloqueados.length > 0 && (
          <Card variant="chip">
            <p className="auth-error" role="alert">
              Estes dias ficariam fora do novo período e têm programação. Nada
              foi alterado.
            </p>
            <ul className="conf-bloqueados">
              {bloqueados.map((d) => (
                <li key={d.date}>
                  {d.date} ·{' '}
                  {[d.activityCount > 0 ? `${d.activityCount} atividade(s)` : null, d.photoCount > 0 ? `${d.photoCount} foto(s)` : null].filter(Boolean).join(' · ')}
                </li>
              ))}
            </ul>
            <p className="auth-note">
              Mova ou exclua o que há nesses dias e tente de novo — ou mantenha o
              período como está.
            </p>
          </Card>
        )}

        {erro && (
          <p className="auth-error" role="alert">
            {erro}
          </p>
        )}

        {salvo && (
          <p className="auth-note" role="status">
            Configurações salvas.
          </p>
        )}

        {podeEditar && (
          <button
            type="submit"
            className="nv-btn nv-btn--primary nv-btn--block"
            disabled={!podeSalvar || enviando}
          >
            {enviando ? 'Salvando…' : 'Salvar alterações'}
          </button>
        )}
      </form>

      {/* Quem participa sai por aqui; o dono transfere a propriedade antes (PO, 19/09). */}
      {trip.role !== 'owner' && (
        <>
          <SectionLabel>SAIR</SectionLabel>
          <SairDaViagem tripId={trip.id} nome={trip.name} formato="largo" />
        </>
      )}
    </section>
  );
}
